import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, Dialog, Stack, Typography } from '@mui/material';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import FormProvider from 'common/components/form-provider';
import BaseTextField from 'common/components/base-text-field';
import { colors, styleInput } from 'common/constants';
import { createStyleSheet, handleError } from 'utils/helpers';
import RequiredLabel from 'common/components/required-label';
import { useDispatch } from 'react-redux';
import { ReactComponent as SyncIcon } from 'assets/images/icons/sync.svg';
import CloseIcon from '@mui/icons-material/Close';
import { common } from '@mui/material/colors';
import { toast } from 'react-toastify';
import { createUniqLink, updateUniqLink } from 'services/uniqLink.service';
import { setIsLoading } from 'slices/loadingSlice';
import { IUniqLink, IUniqLinkPayload } from 'types/uniq-link';
import UniqLinkConstants from '../constants';
import IconButton from '@mui/material/IconButton';
import FileCopyIcon from '@mui/icons-material/FileCopy';

interface UniqLinkFormProps {
    actionType: 'add' | 'edit';
    open: boolean;
    onClose: () => void;
    uniqLink?: IUniqLink | null;
    onRefresh?: () => void;
}

const uniqLinkSchema = yup.object().shape({
    uuid_link: yup
        .string()
        .required('UUID Link là bắt buộc')
        .min(60, 'UUID Link phải tối thiểu 60 ký tự')
        .max(80, 'UUID Link tối đa 80 ký tự'),
    email: yup
        .string()
        .email('Email không hợp lệ')
        .required('Email là bắt buộc'),
    survey_link: yup
        .string()
        .required('Link Survey là bắt buộc')
        .url('Link Survey phải là URL hợp lệ'),
});

const UniqLinkForm: React.FC<UniqLinkFormProps> = ({
    actionType,
    open,
    onClose,
    uniqLink,
    onRefresh,
}) => {
    const methods = useForm({
        resolver: yupResolver(uniqLinkSchema),
        defaultValues: {
            uuid_link: '',
            email: '',
            survey_link: '',
        },
    });

    const {
        handleSubmit,
        reset,
        setValue,
        formState: { errors },
    } = methods;
    const dispatch = useDispatch();

    const generateUUID = () => {
        const newUUID = UniqLinkConstants.generateUUID();
        setValue('uuid_link', newUUID);
    };

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const copyToClipboard = (text: string, label: string) => {
        navigator.clipboard.writeText(text);
        toast.success(`Đã sao chép ${label}`);
    };

    const onSubmit = handleSubmit(async (data) => {
        try {
            dispatch(setIsLoading(true));
            let response;

            if (actionType === 'add') {
                response = await createUniqLink(data as IUniqLinkPayload);
            } else {
                if (!uniqLink?.id) {
                    throw new Error('ID không hợp lệ');
                }
                response = await updateUniqLink(
                    uniqLink.id,
                    data as IUniqLinkPayload
                );
            }

            if (response.success) {
                toast.success(response.message);
                dispatch(setIsLoading(false));
                onClose();
                if (onRefresh) {
                    onRefresh();
                }
            } else {
                throw new Error(response.message);
            }
        } catch (error) {
            dispatch(setIsLoading(false));
            handleError(error);
        }
    });

    useEffect(() => {
        if (actionType === 'edit' && uniqLink?.id) {
            reset({
                uuid_link: uniqLink.code || '',
                email: uniqLink.email,
                survey_link: uniqLink.url_survey || '',
            });
        } else if (actionType === 'add') {
            // Generate new UUID for add action
            const newUUID = UniqLinkConstants.generateUUID();
            reset({
                uuid_link: newUUID,
                email: '',
                survey_link: '',
            });
        }
    }, [actionType, uniqLink, reset]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '20px',
                },
            }}
        >
            <Box sx={styles.container}>
                <Typography variant="h6" sx={styles.title}>
                    {actionType === 'add'
                        ? 'Thêm mới Unique Link'
                        : 'Chỉnh sửa Unique Link'}
                </Typography>

                <FormProvider methods={methods} onSubmit={onSubmit}>
                    <Stack spacing={3} sx={{ mb: 3 }}>
                        <Stack spacing="7px">
                            <Box display="flex" alignItems="center" gap={1}>
                                <RequiredLabel
                                    label="UUID Link"
                                    required={true}
                                />
                                <IconButton
                                    size="small"
                                    onClick={generateUUID}
                                    title="Tạo UUID mới"
                                >
                                    <FileCopyIcon
                                        sx={{
                                            fontSize: '18px',
                                            color: colors.main,
                                        }}
                                    />
                                </IconButton>
                            </Box>
                            <BaseTextField
                                autoComplete="off"
                                name="uuid_link"
                                fullWidth
                                size="small"
                                sx={styles.input}
                                multiline
                                rows={3}
                                placeholder="UUID sẽ tự sinh (60-80 ký tự)"
                                disabled={actionType === 'edit'}
                            />
                            {errors.uuid_link && (
                                <Typography sx={styles.errorText}>
                                    {errors.uuid_link.message}
                                </Typography>
                            )}
                        </Stack>

                        <Stack spacing="7px">
                            <RequiredLabel label="Email" required={true} />
                            <BaseTextField
                                autoComplete="off"
                                name="email"
                                fullWidth
                                size="small"
                                sx={styles.input}
                                placeholder="example@email.com"
                                type="email"
                            />
                            {errors.email && (
                                <Typography sx={styles.errorText}>
                                    {errors.email.message}
                                </Typography>
                            )}
                        </Stack>

                        <Stack spacing="7px">
                            <RequiredLabel
                                label="Link Survey"
                                required={true}
                            />
                            <BaseTextField
                                autoComplete="off"
                                name="survey_link"
                                fullWidth
                                size="small"
                                sx={styles.input}
                                multiline
                                rows={3}
                                placeholder="https://example.com/survey?link={uuid}"
                            />
                            {errors.survey_link && (
                                <Typography sx={styles.errorText}>
                                    {errors.survey_link.message}
                                </Typography>
                            )}
                        </Stack>
                    </Stack>

                    <Stack
                        direction="row"
                        spacing={2}
                        justifyContent="flex-end"
                    >
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={onClose}
                            startIcon={<CloseIcon />}
                            sx={{
                                ...styles.btn,
                                backgroundColor: common.white,
                                border: `2px solid ${colors.main}`,
                                ':hover': {
                                    backgroundColor: common.white,
                                    border: `2px solid ${colors.main}`,
                                },
                            }}
                        >
                            Đóng
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            sx={styles.btn}
                            startIcon={
                                actionType === 'edit' ? <SyncIcon /> : undefined
                            }
                        >
                            {actionType === 'add' ? 'Thêm mới' : 'Cập nhật'}
                        </Button>
                    </Stack>
                </FormProvider>
            </Box>
        </Dialog>
    );
};

const styles = createStyleSheet({
    container: {
        padding: '30px',
    },
    title: {
        fontSize: '24px',
        fontWeight: 700,
        color: colors.main,
        marginBottom: '20px',
    },
    input: {
        '& .MuiInputBase-root': {
            ...styleInput['& .MuiInputBase-root'],
            fontSize: '14px',
        },
    },
    errorText: {
        fontSize: '12px',
        color: colors.textError,
        fontWeight: 400,
    },
    btn: {
        minWidth: '120px',
        height: '44px',
        fontSize: '16px',
        lineHeight: '19px',
        fontWeight: '700',
        borderRadius: '22px',
        textTransform: 'none',
    },
});

export default UniqLinkForm;
